import type { FC } from 'react';
import { Box, Chip, Dialog, IconButton, Tooltip } from '@mui/material';
import { Close } from '@mui/icons-material';
import type { FacebookCreativeAdByRegionDTO  } from '../api/types';
import { CreativePaper } from 'features/creatives';
import ContentPreview from './ContentPreview';
import AccordionDetails from './AccordionDetails';
import { getPlatformIcon } from '../utils/getPlatformIcon';

interface CreativeUsDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  adCreativeId: string;
  winnerConfidencePercent: number;
  longevityDays: number;
  adsCount: number;
  creative: FacebookCreativeAdByRegionDTO;
}

const CreativeUsDetailsDialog: FC<CreativeUsDetailsDialogProps> = ({
  open,
  onClose,
  adCreativeId,
  creative,
  winnerConfidencePercent,
  longevityDays,
  adsCount,
}) => {
  const [ad] = creative.ads;

  return (
    <Dialog
      PaperProps={{
        sx: {
          maxWidth: 980,
          width: '90%',
          height: '90vh',
          p: 0,
          overflowY: 'visible',
        },
      }}
      transitionDuration={{
        appear: 0,
        enter: 0,
        exit: 0,
      }}
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'space-between',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
          <CreativePaper id={`fb-ads-creative-${adCreativeId}-dialog-paper`}>
            <Box sx={{ position: 'relative', display: 'flex', flexGrow: 1, height: '100%' }}>
              <ContentPreview
                ad_library_url={ad.ad_library_url}
                video_preview_image_url={ad.video_preview_image_url}
                original_image_url={ad.original_image_url}
                video_sd_url={ad.video_sd_url}
                ad_id={ad.ad_id}
              />
            </Box>
          </CreativePaper>
        </Box>
        <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Box
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: 'text.primary',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ad.company || 'Unknown company'}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {ad.publisher_platforms.map((platform) => {
                const Icon = getPlatformIcon(platform);
                return (
                  <Tooltip title={platform} key={platform}>
                    <Box>
                      <Icon />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              size="small"
              label={`${Math.round(winnerConfidencePercent)}%`}
              sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: 1 }}
            />
            <Chip
              size="small"
              label={`${longevityDays}d`}
              sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 600, borderRadius: 1 }}
            />
            <Chip
              size="small"
              label={`${adsCount} ads`}
              sx={{ backgroundColor: '#F3E5F5', color: '#6A1B9A', fontWeight: 600, borderRadius: 1 }}
            />
          </Box>
          <AccordionDetails
            isUsRegion
            page_name={ad.page_name}
            ad_creative_bodies={ad.ad_creative_bodies ?? []}
            ad_creative_link_titles={ad.ad_creative_link_titles ?? []}
            branch={ad.branch}
            ad_delivery_start_time={creative.ad_delivery_start_time}
            ad_delivery_stop_time={creative.ad_delivery_stop_time}
            scraped_link={ad.scraped_link}
          />
        </Box>
      </Box>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: -12,
          right: -12,
          zIndex: 1,
          bgcolor: 'background.paper',
          boxShadow: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        <Close color="action" />
      </IconButton>
    </Dialog>
  );
};

export default CreativeUsDetailsDialog;
